'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import { useInfiniteQuery } from '@tanstack/react-query';
import Carousel from '~/components/primitives/Carousel';
import Title from '~/components/utils/Title';
import { ActivityCardPriceDescription } from '~/features/activities/ActivityCard/utils';
import { useI18n } from '~/features/i18n';
import type { StoryblokBlokProps } from '~/storyblok/components/utils';
import type { SB_CategoryGalleryBlockProps } from '~/storyblok/components/types.generated';
import { Filters } from '@funkey/shared';
import type { FilterType } from './shared';
import assert from 'assert';
import { PAGINATION_SIZE, CACHE_DURATION } from './shared';
import Link from 'next/link';
import { I18N_CONF } from '@funkey/locales/config';
import { Icon } from '~/components/primitives';
import { CategoryGalleryLoadingSkeleton } from './loader';
import { useMemo } from 'react';
import { cx } from 'class-variance-authority';
import type { TRPC_ROUTER } from '~/modules/trpc/routers';
import { useTRPCClient } from '~/modules/trpc/client';
import { HISTORY, HREF } from '~/modules/browser/history';
import { useDeviceType } from '~/components/primitives/hooks/useDeviceType';
export const CategoryGalleryBlock: React.FC<Omit<StoryblokBlokProps<SB_CategoryGalleryBlockProps>, 'ctx'> & {
  data?: TRPC_ROUTER.output<typeof TRPC_ROUTER.activities.search>;
  /** We have to make this optional, because this component is mounted in `apps/web/src/storyblok/components/index.tsx`, where we don't have access to the config. */
  config?: {
    filterType: FilterType;
    filterId: number;
    pageSize?: number;
    refetchOnMount?: boolean;
  };
  className?: string;
}> = props => {
  const $ = _c(43);
  assert(props.config, "CategoryGalleryBlock: config is required");
  const i18n = useI18n();
  const trpc = useTRPCClient();
  const {
    isMobileScreen
  } = useDeviceType();
  let t0;
  if ($[0] !== i18n.locale || $[1] !== props.config.filterId || $[2] !== props.config.filterType) {
    t0 = ["CategoryGalleryBlock.activities", i18n.locale, props.config.filterType, props.config.filterId];
    $[0] = i18n.locale;
    $[1] = props.config.filterId;
    $[2] = props.config.filterType;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const t1 = t0 as const;
  let t2;
  if ($[4] !== props.config.pageSize || $[5] !== props.config.refetchOnMount || $[6] !== props.data || $[7] !== t1 || $[8] !== trpc) {
    let t3;
    if ($[10] !== props.data) {
      t3 = props.data ? {
        pages: [props.data],
        pageParams: [0]
      } : undefined;
      $[10] = props.data;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    t2 = {
      queryKey: t1,
      async queryFn(ctx) {
        const [, locale, filterType, filterId] = ctx.queryKey;
        return trpc.activities.search.query({
          locale,
          vatRate: null,
          pagination: {
            pageIdx: ctx.pageParam,
            size: props.config.pageSize ?? PAGINATION_SIZE
          },
          filters: {
            [filterType]: [filterId]
          }
        });
      },
      getNextPageParam(lastPage, _allPages, lastPageParam) {
        if (!lastPage || !lastPage.pageInfo) {
          return;
        }
        if (lastPage.pageInfo.current >= lastPage.pageInfo.totalPages - 1) {
          return;
        }
        return lastPageParam + 1;
      },
      initialPageParam: 0,
      initialData: t3,
      refetchOnMount: props.config.refetchOnMount ?? false,
      refetchOnWindowFocus: false,
      staleTime: CACHE_DURATION
    };
    $[4] = props.config.pageSize;
    $[5] = props.config.refetchOnMount;
    $[6] = props.data;
    $[7] = t1;
    $[8] = trpc;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading
  } = useInfiniteQuery(t2);
  let t3;
  if ($[12] !== data?.pages) {
    t3 = data?.pages.flatMap(_temp).filter(Filters.ok) ?? [];
    $[12] = data?.pages;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const allActivities = t3;
  if (isLoading) {
    let t4;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <CategoryGalleryLoadingSkeleton className="container" />;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    return t4;
  }
  if (!props.data && !allActivities.length) {
    return null;
  }
  let t4;
  if ($[15] !== props.className) {
    t4 = cx("container overflow-hidden bg-white py-3", props.className);
    $[15] = props.className;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== props.blok.title || $[18] !== props.blok.url.url) {
    t5 = <Link href={props.blok.url.url} className="text-xl group-hover:underline">{props.blok.title}</Link>;
    $[17] = props.blok.title;
    $[18] = props.blok.url.url;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Icon name="chevron-right" className="text-tertiary transition-all group-hover:ml-1" />;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t5) {
    t7 = <Title as="h2" size="xl" className="group mb-3 flex items-center leading-none" weight="bold">{t5}{t6}</Title>;
    $[21] = t5;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const t8 = props.data ? undefined : "client-loaded";
  let t9;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      container: "-mx-2"
    };
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== fetchNextPage || $[25] !== hasNextPage || $[26] !== isFetchingNextPage) {
    t10 = () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    };
    $[24] = fetchNextPage;
    $[25] = hasNextPage;
    $[26] = isFetchingNextPage;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== allActivities || $[29] !== i18n.locale || $[30] !== isMobileScreen) {
    let t12;
    if ($[32] !== i18n.locale || $[33] !== isMobileScreen) {
      t12 = activity => {
        const activityHref = I18N_CONF.getTranslatedUrl("activities", {
          locale: i18n.locale,
          slug: activity.slug
        });
        return <Link href={activityHref} scroll={isMobileScreen ? false : undefined} onClick={e => {
          HISTORY.specialPages.setValue(_temp2);
          if (isMobileScreen) {
            return;
          }
          e.preventDefault();
          window.location.href = activityHref;
        }} key={activity.id} className="w-[85%] rounded-xl px-2 text-sm xs:w-1/2 sm:w-1/3 md:w-1/4 lg:w-1/5 xl:w-1/6" data-id={activity.id} data-testid="gallery-activity-card"><div className="relative h-36 w-full bg-blue-50">{!!activity.mediaPath && <Image src={activity.mediaPath} alt={activity.name} className="rounded-xl object-cover transition" fill={true} sizes="(max-width: 430px) 100vw, (max-width: 750px) 50vw, (max-width: 1080px) 33vw, (max-width: 1200px) 25vw, 20vw" />}</div><h3 className="mt-4 font-bold text-wrap text-primary transition-colors">{activity.name}</h3><ActivityCardPriceDescription className="mt-2 text-blue-600" {...activity} /></Link>;
      };
      $[32] = i18n.locale;
      $[33] = isMobileScreen;
      $[34] = t12;
    } else {
      t12 = $[34];
    }
    t11 = allActivities.map(t12);
    $[28] = allActivities;
    $[29] = i18n.locale;
    $[30] = isMobileScreen;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[35] !== t10 || $[36] !== t11 || $[37] !== t8) {
    t12 = <Carousel key={t8} classNames={t9} buttons={true} spacing="NONE" disableMarginLeft={true} onPrefetchThreshold={t10} prefetchThreshold={1} slidesToScroll={1}>{t11}</Carousel>;
    $[35] = t10;
    $[36] = t11;
    $[37] = t8;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== t12 || $[40] !== t4 || $[41] !== t7) {
    t13 = <div className={t4}>{t7}{t12}</div>;
    $[39] = t12;
    $[40] = t4;
    $[41] = t7;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  return t13;
};
function _temp(page) {
  return page?.activities;
}
function _temp2(prev) {
  return {
    ...prev,
    "PDP.backToOverview": {
      ...HREF.parseURL(window.location),
      portal: "WEB"
    }
  };
}